import React from 'react';
import styled from 'styled-components';
import { ThinkingOrb } from 'thinking-orbs';

type Props = {
  text?: string | React.ReactNode;
  onHide?: () => void;
  hideText?: string;
  /**
   * Accessible name for the indicator. Defaults to `text` when that is a
   * plain string — the orb is a canvas, so without a name it is invisible to
   * assistive tech.
   */
  ariaLabel?: string;
};

/**
 * The solving orb and its label, centred in whatever region hosts it — the orb
 * counterpart to `LoadingState` in `common/Spinner`, and the only place the orb
 * is constructed. `FullScreenLoadingOverlay` is this on an opaque full-viewport
 * field; reach for this directly when the wait belongs to one region and the
 * rest of the shell is still useful.
 *
 * Use it over `LoadingState`'s ring when the wait is sustained (past ~10s with
 * no percentage to report) — a ring promises a moment. Where a real percentage
 * exists a determinate `ProgressBar` beats both.
 *
 * `theme` is pinned rather than left on the orb's `auto` default: auto falls
 * back to `prefers-color-scheme`, and the platform has no light theme, so on a
 * viewer whose OS is set to light it would paint dark ink on a dark field.
 */
export const OrbLoadingState = ({
  text,
  onHide,
  hideText = 'Hide',
  ariaLabel,
}: Props) => (
  <Region role="status" aria-live="polite">
    <ThinkingOrb
      state="solving"
      size={64}
      theme="dark"
      aria-label={ariaLabel ?? (typeof text === 'string' ? text : 'Loading')}
    />
    {!!text && <Text>{text}</Text>}
    {!!onHide && (
      <HideButton type="button" onClick={onHide}>
        {hideText}
      </HideButton>
    )}
  </Region>
);

export default OrbLoadingState;

// Fills its region and centres in it, so the same component works as the body
// of the full-screen overlay and as the contents of a tab pane that is still
// fetching. No card and no scrim of its own: the region it replaces is empty,
// so there is nothing to obscure — a panel floating in it is a border around
// nothing.
const Region = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 240px;
`;

const Text = styled.p`
  margin: 0;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
`;

// Ghost button, sm. The design system names "Hide Waveform / Hide Video" as
// its Ghost example, so this escape hatch is a Ghost — not the green
// underlined div SpinningLoading rendered, which was neither focusable nor
// entitled to --primary.
const HideButton = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border: none;
  border-radius: var(--radius-button);
  background: none;
  color: var(--text-muted);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;
